import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

// Same display durations as product-form-component, kept in sync on purpose.
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;
const ERROR_BUTTON_REENABLE_DELAY = 1000;
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * Generates a random id shared between the bundle line and the separate
 * subscription line, when there is one - just an internal correlation id
 * for merchant/support reference, doesn't affect pricing or display.
 * @returns {string}
 */
function generateBundleId() {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * @typedef {object} BundlePick
 * @property {string} label - the option group's label (e.g. "First bag color")
 * @property {string} title - the picked product's display name (e.g. "Green")
 * @property {string} productTitle - the real product title (e.g. "The Bread Bag")
 * @property {string} image - small image url, may be empty string
 *
 * @typedef {object} BundleSubscriptionPick
 * @property {string} variantId
 * @property {string} label
 * @property {string} title
 * @property {string} productTitle
 * @property {string} image
 * @property {string | null} sellingPlanId
 * @property {boolean} isSubscription
 */

/**
 * Reads the option group's label from bundle-picker.liquid
 * (.bundle-picker__group > .bundle-picker__option-label).
 * @param {Element} el
 * @returns {string}
 */
function getGroupLabel(el) {
	const group = el.closest('.bundle-picker__group');
	const labelEl = group?.querySelector('.bundle-picker__option-label');
	const text = labelEl?.textContent?.trim().replace(/:\s*$/, '');
	return text || 'Bundle item';
}

/**
 * @param {Element} root
 * @returns {{ groupItems: BundlePick[], subscriptionItems: BundleSubscriptionPick[] }}
 */
function readBundleSelection(root) {
	/** @type {BundlePick[]} */
	const groupItems = [];
	const seenGroupNames = new Set();

	root.querySelectorAll('input[name^="bundle_option_"]:checked').forEach((input) => {
		if (!(input instanceof HTMLInputElement)) return;
		if (seenGroupNames.has(input.name)) return;
		seenGroupNames.add(input.name);

		// data-title should always be set by bundle-option-item.liquid, but if
		// it's ever missing (e.g. bundle_product resolved to nil because the
		// custom_bundle_set metaobject has a blank product for this slot),
		// don't silently drop the pick - fall back to something usable and
		// say so loudly, instead of just not adding a property at all.
		const title = input.dataset.title || input.dataset.productId || input.value;
		if (!input.dataset.title) {
			console.warn(
				`bundle-product-form-component: "${input.name}" has no data-title, using "${title}" instead. Check bundle-option-item.liquid / the underlying product in custom_bundle_set.`
			);
		}
		groupItems.push({
			label: getGroupLabel(input),
			title,
			productTitle: input.dataset.productTitle || title,
			image: input.dataset.image || '',
		});
	});

	/** @type {BundleSubscriptionPick[]} */
	const subscriptionItems = [];

	root.querySelectorAll('.bundle-subscription-widget').forEach((widget) => {
		if (!(widget instanceof HTMLElement)) return;

		const variantId = widget.dataset.variantId;
		if (!variantId) {
			console.warn(
				'bundle-product-form-component: found a .bundle-subscription-widget with no data-variant-id, skipping it. Check the underlying product in custom_bundle_set.'
			);
			return;
		}

		const title = widget.dataset.title || widget.dataset.bundleProductId || variantId;
		if (!widget.dataset.title) {
			console.warn(
				`bundle-product-form-component: subscription widget for variant ${variantId} has no data-title, using "${title}" instead.`
			);
		}

		const label = getGroupLabel(widget);
		const productTitle = widget.dataset.productTitle || title;
		const image = widget.dataset.image || '';

		const subscribeRadio = /** @type {HTMLInputElement | null} */ (
			widget.querySelector('input[name^="bundle_purchase_option_"][value="subscription"]')
		);
		const isSubscription = !!subscribeRadio?.checked;

		if (isSubscription) {
			const select = /** @type {HTMLSelectElement | null} */ (widget.querySelector('[data-selling-plan-select]'));
			subscriptionItems.push({
				variantId,
				label,
				title,
				productTitle,
				image,
				sellingPlanId: select?.value || null,
				isSubscription: true,
			});
		} else {
			subscriptionItems.push({
				variantId,
				label,
				title,
				productTitle,
				image,
				sellingPlanId: null,
				isSubscription: false,
			});
		}
	});

	return { groupItems, subscriptionItems };
}

/**
 * Lightweight version of readBundleSelection() used only for price display
 * (see #updateBundlePrices below) - we don't need the full picks list here,
 * just whether the bundle's subscription widget is currently set to
 * "Subscribe & Save" and, if so, which selling plan is selected in its
 * dropdown. Assumes a single .bundle-subscription-widget per bundle, same
 * as snippets/price.liquid assumes a single subscription-capable product
 * when it computes data-bundle-with-subscription-price.
 * @param {Element} root - the [data-bundle-picker] element
 * @returns {{ isSubscription: boolean, sellingPlanId: string | null }}
 */
function readBundlePriceSelection(root) {
	const widget = root.querySelector('.bundle-subscription-widget');
	if (!widget) return { isSubscription: false, sellingPlanId: null };

	const subscribeRadio = /** @type {HTMLInputElement | null} */ (
		widget.querySelector('input[name^="bundle_purchase_option_"][value="subscription"]')
	);
	const isSubscription = !!subscribeRadio?.checked;

	if (!isSubscription) return { isSubscription: false, sellingPlanId: null };

	const select = /** @type {HTMLSelectElement | null} */ (widget.querySelector('[data-selling-plan-select]'));
	return { isSubscription: true, sellingPlanId: select?.value || null };
}

/**
 * A custom element that assembles the checked bundle-picker inputs into a
 * single (or dual, when a subscription is involved) /cart/add.js call.
 *
 * Uses the theme's real quantity-selector-component (from the reused
 * "quantity" content block) instead of a plain <input> - same as
 * product-form-component does - so read the value via
 * quantitySelector.getValue() rather than a ref.
 *
 * @typedef {object} BundleProductFormRefs
 * @property {(HTMLElement & { refs?: { addToCartButton?: HTMLButtonElement }, disable?: () => void, enable?: () => void, animateAddToCart?: () => void }) | undefined} addToCartButtonContainer
 * @property {HTMLElement | undefined} addToCartTextError
 * @property {HTMLElement} liveRegion
 *
 * @extends Component<BundleProductFormRefs>
 */
class BundleProductFormComponent extends Component {
	requiredRefs = ['liveRegion'];

	/** @type {number | undefined} */
	#timeout;

	connectedCallback() {
		super.connectedCallback?.();

		// Bundle price display (Subscribe & Save toggle) - see the "Bundle
		// price override" block in snippets/price.liquid for where the
		// data-bundle-* attributes read below come from. This is purely
		// display: it doesn't touch #handleSubmit / the actual cart payload.
		this.#bindBundlePriceListeners();
		this.#updateBundlePrices();
	}

	/**
	 * Watches the bundle's subscription widget (the "Subscribe & Save" /
	 * "One-time purchase" radios and the frequency <select>) for changes and
	 * re-renders the price block(s) in this section.
	 */
	#bindBundlePriceListeners() {
		const root = this.closest('.shopify-section')?.querySelector('[data-bundle-picker]');
		if (!root) return;

		root.addEventListener('change', () => this.#updateBundlePrices());
	}

	/**
	 * Swaps the text of every [data-bundle-price-value] price element in this
	 * section between the bundle's normal price and the pre-computed
	 * "bundle without the subscription item + that item's subscription
	 * price" total, based on the current subscription widget selection.
	 * The compare-at price is intentionally left untouched here - it's
	 * always the bundle's own compare_at_price, computed once in Liquid.
	 */
	#updateBundlePrices() {
		const root = this.closest('.shopify-section')?.querySelector('[data-bundle-picker]');
		if (!root) return;

		const priceContainers = this.closest('.shopify-section')?.querySelectorAll('[data-bundle-product]');
		if (!priceContainers || priceContainers.length === 0) return;

		const { isSubscription, sellingPlanId } = readBundlePriceSelection(root);

		priceContainers.forEach((container) => {
			if (!(container instanceof HTMLElement)) return;

			const priceEl = container.querySelector('[data-bundle-price-value]');
			if (!priceEl) return;

			if (!isSubscription) {
				if (container.dataset.bundleOriginalPrice) {
					priceEl.textContent = container.dataset.bundleOriginalPrice;
				}
				return;
			}

			/** @type {Record<string, string>} */
			let planPrices = {};
			try {
				planPrices = JSON.parse(container.dataset.bundleSubscriptionPlanPrices || '{}');
			} catch (error) {
				console.warn(
					'bundle-product-form-component: could not parse data-bundle-subscription-plan-prices as JSON.',
					error
				);
			}

			const price = (sellingPlanId && planPrices[sellingPlanId]) || container.dataset.bundleWithSubscriptionPrice;

			if (!price) {
				console.warn(
					'bundle-product-form-component: no bundle-with-subscription price available for the selected selling plan - check data-bundle-subscription-plan-prices / data-bundle-with-subscription-price on the price block (see snippets/price.liquid).'
				);
				return;
			}

			priceEl.textContent = price;
		});
	}

	/**
	 * @param {Event} event
	 */
	handleSubmit(event) {
		event.preventDefault();

		const { addToCartButtonContainer, addToCartTextError } = this.refs;

		if (addToCartButtonContainer?.refs?.addToCartButton?.disabled) return;

		const root = this.closest('.shopify-section')?.querySelector('[data-bundle-picker]');
		if (!root) {
			console.error('bundle-product-form-component: no [data-bundle-picker] found in this section');
			return;
		}

		const { groupItems, subscriptionItems } = readBundleSelection(root);

		if (groupItems.length === 0 && subscriptionItems.length === 0) {
			console.warn(
				'bundle-product-form-component: no checked bundle_option_* inputs and no .bundle-subscription-widget found at all - check that bundle-picker.liquid actually rendered any groups (see its console warnings about custom_bundle_set).'
			);
		}

		const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
		const quantity = Number(quantitySelector?.getValue?.() ?? 1) || 1;

		const hasSubscriptionSelected = subscriptionItems.some((item) => item.isSubscription);
		const bundleId = generateBundleId();

		const mainVariantId = hasSubscriptionSelected
			? this.dataset.bundleWithoutSubscriptionVariantId
			: this.dataset.fullBundleVariantId;

		if (!mainVariantId) {
			console.error('bundle-product-form-component: no bundle container variant id configured');
			return;
		}

		// Every non-subscription pick (color/button groups, plus the soap when
		// it's bought one-time) becomes:
		//   1. a plain, VISIBLE property keyed by the REAL product title, valued
		//      by the picked variant/color name (e.g. "The Bread Bag": "Green") -
		//      Shopify shows this natively in cart/checkout/order.
		//   2. a HIDDEN pair of companion properties keyed off that same
		//      property key (_bundle_product_title:<key> / _bundle_product_image:<key>)
		//      carrying the REAL product title + a photo, so the cart page can
		//      render an actual product-looking sub-item instead of just text.
		//      Liquid can't parse a JSON blob back out of a property, which is
		//      why this is flat key/value pairs instead of one JSON property.
		//
		// Two picks can share the same product title (e.g. "First bag color"
		// and "Second bag color" are both "The Bread Bag", just different
		// colors) - since properties are a flat object, using the title alone
		// as the key would let the second pick silently overwrite the first.
		// uniquePropertyKey() appends " (2)", " (3)", etc. when that happens,
		// so nothing gets lost - the displayed title itself is unaffected,
		// since the cart page reads it back from the hidden companion
		// property, not from the key itself.
		/** @type {Record<string, string>} */
		const mainProperties = { _bundle_id: bundleId };

		/**
		 * @param {string} title
		 * @returns {string}
		 */
		const uniquePropertyKey = (title) => {
			let key = title;
			let suffix = 2;
			while (Object.prototype.hasOwnProperty.call(mainProperties, key)) {
				key = `${title} (${suffix})`;
				suffix += 1;
			}
			return key;
		};

		/** @param {{label: string, title: string, productTitle: string, image: string}} item */
		const addPickProperties = (item) => {
			const key = uniquePropertyKey(item.productTitle);
			mainProperties[key] = item.title;
			mainProperties[`_bundle_product_title:${key}`] = item.productTitle;
			if (item.image) mainProperties[`_bundle_product_image:${key}`] = item.image;
		};

		groupItems.forEach(addPickProperties);
		subscriptionItems.filter((item) => !item.isSubscription).forEach(addPickProperties);

		if (Object.keys(mainProperties).length <= 1) {
			// Only _bundle_id got in - nothing from groupItems/subscriptionItems
			// made it into mainProperties, which is the exact "properties don't
			// get added" symptom.
			console.warn(
				'bundle-product-form-component: mainProperties ended up with nothing but _bundle_id - no picks were read from the picker. See earlier warnings for which group is missing data.'
			);
		}

		/** @type {Array<{id: string, quantity: number, properties: Record<string, string>, selling_plan?: string}>} */
		const items = [{ id: mainVariantId, quantity, properties: mainProperties }];

		// The subscription pick has to be its OWN separate cart line - that's
		// a hard Shopify rule, not a choice: a line carrying a selling_plan can
		// never be merged into another line.
		if (hasSubscriptionSelected) {
			subscriptionItems
				.filter((item) => item.isSubscription)
				.forEach((item) => {
					/** @type {{id: string, quantity: number, properties: Record<string, string>, selling_plan?: string}} */
					const line = { id: item.variantId, quantity, properties: { _bundle_id: bundleId } };
					if (item.sellingPlanId) line.selling_plan = item.sellingPlanId;
					items.push(line);
				});
		}

		this.#addToCart(items);
	}

	/**
	 * @param {Array<{id: string, quantity: number, properties: Record<string, string>, selling_plan?: string}>} items
	 */
	#addToCart(items) {
		const { addToCartButtonContainer } = this.refs;

		addToCartButtonContainer?.disable();

		/** @type {string[]} */
		const sectionIds = [];
		document.querySelectorAll('cart-items-component').forEach((el) => {
			if (el instanceof HTMLElement && typeof el.dataset.sectionId === 'string') {
				sectionIds.push(el.dataset.sectionId);
			}
		});

		fetch('/cart/add.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify({ items, sections: sectionIds.join(',') }),
		})
			.then((response) => response.json())
			.then((response) => {
				if (response.status) {
					this.#handleError(response);
					return;
				}

				this.#handleSuccess(items, response);
			})
			.catch((error) => {
				console.error(error);
				addToCartButtonContainer?.enable();
			});
	}

	/**
	 * @param {{message: string, description?: string, errors?: Record<string, string>}} response
	 */
	#handleError(response) {
		const { addToCartButtonContainer, addToCartTextError } = this.refs;

		this.dispatchEvent(
			new CartErrorEvent(this.id, response.message, /** @type {any} */(response.description), response.errors)
		);

		if (addToCartTextError) {
			addToCartTextError.classList.remove('hidden');

			const textNode = addToCartTextError.childNodes[2];
			if (textNode) {
				textNode.textContent = response.message;
			} else {
				addToCartTextError.appendChild(document.createTextNode(response.message));
			}

			this.#setLiveRegionText(response.message);

			if (this.#timeout) clearTimeout(this.#timeout);
			this.#timeout = setTimeout(() => {
				addToCartTextError.classList.add('hidden');
				this.#clearLiveRegionText();
			}, ERROR_MESSAGE_DISPLAY_DURATION);
		}

		setTimeout(() => {
			this.refs.addToCartButtonContainer?.enable();
		}, ERROR_BUTTON_REENABLE_DELAY);
	}

	/**
	 * @param {Array<{quantity: number}>} items
	 * @param {{sections?: Record<string, string>}} response
	 */
	#handleSuccess(items, response) {
		const { addToCartButtonContainer, addToCartTextError } = this.refs;

		if (addToCartTextError) {
			addToCartTextError.classList.add('hidden');
			addToCartTextError.removeAttribute('aria-live');
		}

		addToCartButtonContainer?.animateAddToCart?.();

		const addedText = /** @type {any} */ (window).Theme?.translations?.added || 'Added to cart';
		this.#setLiveRegionText(addedText);
		setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);

		const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

		this.dispatchEvent(
			new CartAddEvent({}, this.id, {
				source: 'bundle-product-form-component',
				itemCount,
				sections: response.sections,
			})
		);

		addToCartButtonContainer?.enable();
	}

	/** @param {string} text */
	#setLiveRegionText(text) {
		this.refs.liveRegion.textContent = text;
	}

	#clearLiveRegionText() {
		this.refs.liveRegion.textContent = '';
	}
}

if (!customElements.get('bundle-product-form-component')) {
	customElements.define('bundle-product-form-component', BundleProductFormComponent);
}